import React, { useState, useEffect, useCallback } from 'react';
import {
  Send, ChevronDown, ChevronUp, Save, RotateCcw, Loader2,
  Tag, Eye, Users, Mail, Power, Clock, History, AlertTriangle, TestTube2
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import { toast } from 'sonner';

const CATEGORY_COLORS = {
  onboarding: 'bg-blue-100 text-blue-800',
  activation: 'bg-orange-100 text-orange-800',
  content: 'bg-green-100 text-green-800',
  engagement: 'bg-purple-100 text-purple-800',
};

const CADENCE_LABELS = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  drip: 'Drip (after signup)',
};

const SAMPLE_TAGS = {
  '[firstname]': 'John',
  '[lastname]': 'Doe',
  '[email]': 'john@example.com',
  '[login_link]': '#',
  '[unsubscribe_link]': '#',
  '[current_tier]': 'Bronze',
  '[next_tier]': 'Silver',
  '[company_name]': 'Trados',
  '[company_url]': 'https://www.trados.app',
  '[company_email]': 'info@trados.app',
  '[date]': new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
  '[articles_block]': '<div style="border:1px dashed #cbd5e1;border-radius:8px;padding:12px;color:#64748b;">Latest articles appear here</div>',
  '[deals_block]': '<div style="border:1px dashed #cbd5e1;border-radius:8px;padding:12px;color:#64748b;">New deals appear here</div>',
  '[stats_block]': '<div style="border:1px dashed #cbd5e1;border-radius:8px;padding:12px;color:#64748b;">Platform stats appear here</div>',
  '[trados_logo]': '',
  '[company_logo]': '',
};

const applySampleTags = (html) => {
  let out = html || '';
  Object.entries(SAMPLE_TAGS).forEach(([tag, value]) => {
    out = out.split(tag).join(value);
  });
  return out;
};

const AdminNewsletters = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [masterEnabled, setMasterEnabled] = useState(false);
  const [weekdays, setWeekdays] = useState(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(null);
  const [testing, setTesting] = useState(null);
  const [sendingNow, setSendingNow] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [audience, setAudience] = useState({});
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await api.get('/admin/newsletters');
      setCampaigns(res.data.campaigns || []);
      setMasterEnabled(res.data.master_enabled || false);
      if (res.data.weekdays) setWeekdays(res.data.weekdays);
    } catch (error) {
      toast.error('Failed to load newsletter campaigns');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleToggleMaster = async () => {
    const next = !masterEnabled;
    try {
      await api.put('/admin/newsletters/master', { enabled: next });
      setMasterEnabled(next);
      toast.success(next ? 'Newsletters module enabled' : 'Newsletters module disabled');
    } catch (error) {
      toast.error('Failed to update master switch');
    }
  };

  const toggleExpand = (key) => {
    if (expanded === key) {
      setExpanded(null);
      setShowPreview(false);
      return;
    }
    const c = campaigns.find((x) => x.campaign_key === key);
    setEditData({
      subject: c?.subject || '',
      body_html: c?.body_html || '',
      schedule: { ...(c?.schedule || {}) },
    });
    setExpanded(key);
    setShowPreview(false);
    if (audience[key] === undefined) {
      api.get(`/admin/newsletters/${key}/audience`)
        .then((res) => setAudience((prev) => ({ ...prev, [key]: res.data })))
        .catch(() => setAudience((prev) => ({ ...prev, [key]: { count: '—', sample: [] } })));
    }
  };

  const handleToggleCampaign = async (campaign, e) => {
    e.stopPropagation();
    const next = !campaign.enabled;
    try {
      await api.put(`/admin/newsletters/${campaign.campaign_key}`, { enabled: next });
      setCampaigns((prev) => prev.map((c) => (
        c.campaign_key === campaign.campaign_key ? { ...c, enabled: next } : c
      )));
      toast.success(`${campaign.title} ${next ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update campaign');
    }
  };

  const handleSave = async (campaign) => {
    setSaving(campaign.campaign_key);
    try {
      const res = await api.put(`/admin/newsletters/${campaign.campaign_key}`, {
        subject: editData.subject,
        body_html: editData.body_html,
        schedule: editData.schedule,
      });
      setCampaigns((prev) => prev.map((c) => (
        c.campaign_key === campaign.campaign_key ? { ...c, ...res.data.campaign } : c
      )));
      toast.success('Campaign saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save campaign');
    } finally {
      setSaving(null);
    }
  };

  const handleReset = async (campaign) => {
    if (!window.confirm(`Reset "${campaign.title}" subject and body to defaults? Schedule and enabled state are kept.`)) return;
    try {
      const res = await api.post(`/admin/newsletters/${campaign.campaign_key}/reset`);
      const updated = res.data.campaign;
      setCampaigns((prev) => prev.map((c) => (
        c.campaign_key === campaign.campaign_key ? { ...c, ...updated } : c
      )));
      setEditData({
        subject: updated.subject || '',
        body_html: updated.body_html || '',
        schedule: { ...(updated.schedule || {}) },
      });
      toast.success('Campaign template reset to default');
    } catch (error) {
      toast.error('Failed to reset campaign');
    }
  };

  const handleTest = async (campaign) => {
    setTesting(campaign.campaign_key);
    try {
      const res = await api.post(`/admin/newsletters/${campaign.campaign_key}/test`);
      toast.success(res.data.message || 'Test email sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test send failed');
    } finally {
      setTesting(null);
    }
  };

  const handleSendNow = async (campaign) => {
    const info = audience[campaign.campaign_key];
    const count = info?.count ?? 'the matched';
    if (!window.confirm(`Send "${campaign.title}" NOW to ${count} recipients? Unsubscribed users and recent duplicates are skipped automatically.`)) return;
    setSendingNow(campaign.campaign_key);
    try {
      const res = await api.post(`/admin/newsletters/${campaign.campaign_key}/send-now`);
      toast.success(res.data.message || 'Campaign started');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start campaign');
    } finally {
      setSendingNow(null);
    }
  };

  const fetchLogs = async () => {
    setShowLogs(true);
    setLogsLoading(true);
    try {
      const res = await api.get('/admin/newsletters/logs?limit=100');
      setLogs(res.data.logs || []);
    } catch (error) {
      toast.error('Failed to load send logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const updateSchedule = (field, value) => {
    setEditData((prev) => ({
      ...prev,
      schedule: { ...prev.schedule, [field]: parseInt(value, 10) },
    }));
  };

  const scheduleSummary = (campaign) => {
    const s = campaign.schedule || {};
    const hour = `${String(s.hour ?? 10).padStart(2, '0')}:00 UTC`;
    if (campaign.cadence === 'weekly') return `${weekdays[s.day_of_week ?? 0]} at ${hour}`;
    if (campaign.cadence === 'monthly') return `Day ${s.day_of_month ?? 1} of month at ${hour}`;
    if (campaign.cadence === 'drip') return `${s.days_after ?? 1} day(s) after signup at ${hour}`;
    return hour;
  };

  const hourOptions = Array.from({ length: 24 }, (_, i) => i);

  if (loading) {
    return (
      <Sidebar>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" data-testid="newsletters-loading" />
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-6 md:p-8 bg-gray-50/50 min-h-screen">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Send className="w-6 h-6 text-[#2B4B6A]" /> Newsletters
              </h1>
              <p className="text-gray-500 mt-1">
                Recurring email campaigns: digests, activation nudges, and re-engagement. Templates use the shared email frame from Email Templates.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchLogs}
                data-testid="newsletters-logs-btn"
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 bg-white"
              >
                <History className="w-4 h-4" /> Send Log
              </button>
              <button
                onClick={handleToggleMaster}
                data-testid="newsletters-master-toggle"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold ${
                  masterEnabled ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <Power className="w-4 h-4" /> {masterEnabled ? 'Module ON' : 'Module OFF'}
              </button>
            </div>
          </div>

          {/* Master-off warning */}
          {!masterEnabled && (
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6" data-testid="newsletters-master-warning">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                The newsletters module is <strong>disabled</strong>. No scheduled campaigns will send, even if individually enabled.
                You can still edit templates, preview, and send test emails. Turn on the master switch when everything is ready.
              </p>
            </div>
          )}

          {/* Send log panel */}
          {showLogs && (
            <div className="bg-white border border-gray-200 rounded-xl mb-6 overflow-hidden" data-testid="newsletters-logs-panel">
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <History className="w-4 h-4 text-gray-500" /> Recent Sends
                </h2>
                <button onClick={() => setShowLogs(false)} className="text-sm text-gray-500 hover:text-gray-700">Close</button>
              </div>
              {logsLoading ? (
                <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-gray-400" /></div>
              ) : logs.length === 0 ? (
                <p className="p-6 text-sm text-gray-500 text-center">No newsletter sends yet.</p>
              ) : (
                <div className="overflow-x-auto max-h-80 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-500 text-left sticky top-0">
                      <tr>
                        <th className="px-5 py-2 font-medium">Campaign</th>
                        <th className="px-5 py-2 font-medium">Recipient</th>
                        <th className="px-5 py-2 font-medium">Status</th>
                        <th className="px-5 py-2 font-medium">Trigger</th>
                        <th className="px-5 py-2 font-medium">Sent At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr key={log.id || log._id} className="border-t border-gray-100">
                          <td className="px-5 py-2 text-gray-900">{log.campaign_key}</td>
                          <td className="px-5 py-2 text-gray-600">{log.email}</td>
                          <td className="px-5 py-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              log.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>{log.status}</span>
                          </td>
                          <td className="px-5 py-2 text-gray-500">{log.triggered_by}</td>
                          <td className="px-5 py-2 text-gray-500">{log.sent_at ? new Date(log.sent_at).toLocaleString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Campaign list */}
          <div className="space-y-3">
            {campaigns.map((campaign) => {
              const isOpen = expanded === campaign.campaign_key;
              const aud = audience[campaign.campaign_key];
              return (
                <div key={campaign.campaign_key} className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={`campaign-row-${campaign.campaign_key}`}>
                  {/* Row header */}
                  <div
                    className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleExpand(campaign.campaign_key)}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <button
                        onClick={(e) => handleToggleCampaign(campaign, e)}
                        data-testid={`campaign-toggle-${campaign.campaign_key}`}
                        className={`relative w-10 h-6 rounded-full transition-colors flex-shrink-0 ${campaign.enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                        title={campaign.enabled ? 'Disable campaign' : 'Enable campaign'}
                      >
                        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${campaign.enabled ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                      </button>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-gray-900">{campaign.title}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CATEGORY_COLORS[campaign.category] || 'bg-gray-100 text-gray-700'}`}>
                            {campaign.category}
                          </span>
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            {CADENCE_LABELS[campaign.cadence] || campaign.cadence}
                          </span>
                          {campaign.is_customized && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">customized</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 truncate mt-0.5">{campaign.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0 ml-4">
                      <div className="hidden md:flex items-center gap-1.5 text-xs text-gray-500">
                        <Clock className="w-3.5 h-3.5" /> {scheduleSummary(campaign)}
                      </div>
                      <div className="hidden md:flex items-center gap-1.5 text-xs text-gray-500">
                        <Mail className="w-3.5 h-3.5" /> {campaign.sent_30d} sent / 30d
                      </div>
                      {isOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </div>
                  </div>

                  {/* Expanded editor */}
                  {isOpen && (
                    <div className="border-t border-gray-100 px-5 py-5 space-y-5" data-testid={`campaign-editor-${campaign.campaign_key}`}>
                      {/* Schedule + audience */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2 flex flex-wrap items-end gap-3">
                          {campaign.cadence === 'weekly' && (
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">Day of week</label>
                              <select
                                value={editData.schedule?.day_of_week ?? 0}
                                onChange={(e) => updateSchedule('day_of_week', e.target.value)}
                                data-testid={`schedule-day-${campaign.campaign_key}`}
                                className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                              >
                                {weekdays.map((d, i) => <option key={d} value={i}>{d}</option>)}
                              </select>
                            </div>
                          )}
                          {campaign.cadence === 'monthly' && (
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">Day of month</label>
                              <select
                                value={editData.schedule?.day_of_month ?? 1}
                                onChange={(e) => updateSchedule('day_of_month', e.target.value)}
                                data-testid={`schedule-day-${campaign.campaign_key}`}
                                className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                              >
                                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
                              </select>
                            </div>
                          )}
                          {campaign.cadence === 'drip' && (
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">Days after signup</label>
                              <select
                                value={editData.schedule?.days_after ?? 1}
                                onChange={(e) => updateSchedule('days_after', e.target.value)}
                                data-testid={`schedule-day-${campaign.campaign_key}`}
                                className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                              >
                                {Array.from({ length: 31 }, (_, i) => i).map((d) => <option key={d} value={d}>{d}</option>)}
                              </select>
                            </div>
                          )}
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Hour (UTC)</label>
                            <select
                              value={editData.schedule?.hour ?? 10}
                              onChange={(e) => updateSchedule('hour', e.target.value)}
                              data-testid={`schedule-hour-${campaign.campaign_key}`}
                              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                            >
                              {hourOptions.map((h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
                            </select>
                          </div>
                          {campaign.last_sent_at && (
                            <p className="text-xs text-gray-400 pb-2">Last run: {new Date(campaign.last_sent_at).toLocaleString()}</p>
                          )}
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-3" data-testid={`audience-box-${campaign.campaign_key}`}>
                          <Users className="w-5 h-5 text-[#2B4B6A] flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              {aud === undefined ? <Loader2 className="w-4 h-4 animate-spin inline" /> : `${aud.count} recipients`}
                            </p>
                            <p className="text-xs text-gray-500">match this audience right now</p>
                          </div>
                        </div>
                      </div>

                      {/* Tags */}
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1.5 flex items-center gap-1">
                          <Tag className="w-3.5 h-3.5" /> Available tags (click to copy)
                        </label>
                        <div className="flex flex-wrap gap-1.5">
                          {(campaign.available_tags || []).map((tag) => (
                            <button
                              key={tag}
                              onClick={() => { navigator.clipboard.writeText(tag); toast.success(`Copied ${tag}`); }}
                              className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-mono hover:bg-blue-100"
                            >
                              {tag}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Subject */}
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Subject</label>
                        <input
                          type="text"
                          value={editData.subject}
                          onChange={(e) => setEditData((prev) => ({ ...prev, subject: e.target.value }))}
                          data-testid={`subject-input-${campaign.campaign_key}`}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>

                      {/* Body */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-xs font-medium text-gray-500">Body HTML</label>
                          <button
                            onClick={() => setShowPreview(!showPreview)}
                            data-testid={`preview-toggle-${campaign.campaign_key}`}
                            className="flex items-center gap-1 text-xs text-[#2B4B6A] hover:underline"
                          >
                            <Eye className="w-3.5 h-3.5" /> {showPreview ? 'Hide preview' : 'Show preview'}
                          </button>
                        </div>
                        <textarea
                          value={editData.body_html}
                          onChange={(e) => setEditData((prev) => ({ ...prev, body_html: e.target.value }))}
                          data-testid={`body-input-${campaign.campaign_key}`}
                          rows={12}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs font-mono"
                        />
                        {showPreview && (
                          <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
                            <div className="bg-gray-50 px-3 py-1.5 text-xs text-gray-500 border-b border-gray-200">
                              Preview with sample data — actual email is wrapped in the shared frame
                            </div>
                            <div
                              className="p-4 bg-white max-h-96 overflow-y-auto"
                              style={{ maxWidth: 600, margin: '0 auto' }}
                              dangerouslySetInnerHTML={{ __html: applySampleTags(editData.body_html) }}
                            />
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex flex-wrap items-center gap-2 pt-1">
                        <button
                          onClick={() => handleSave(campaign)}
                          disabled={saving === campaign.campaign_key}
                          data-testid={`save-btn-${campaign.campaign_key}`}
                          className="flex items-center gap-2 px-4 py-2 bg-[#2B4B6A] text-white rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                        >
                          {saving === campaign.campaign_key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          Save
                        </button>
                        <button
                          onClick={() => handleTest(campaign)}
                          disabled={testing === campaign.campaign_key}
                          data-testid={`test-btn-${campaign.campaign_key}`}
                          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                        >
                          {testing === campaign.campaign_key ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube2 className="w-4 h-4" />}
                          Send Test to Me
                        </button>
                        <button
                          onClick={() => handleSendNow(campaign)}
                          disabled={sendingNow === campaign.campaign_key}
                          data-testid={`send-now-btn-${campaign.campaign_key}`}
                          className="flex items-center gap-2 px-4 py-2 border border-amber-300 bg-amber-50 rounded-lg text-sm text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                        >
                          {sendingNow === campaign.campaign_key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                          Send Now
                        </button>
                        {campaign.is_customized && (
                          <button
                            onClick={() => handleReset(campaign)}
                            data-testid={`reset-btn-${campaign.campaign_key}`}
                            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-500 hover:bg-gray-100 ml-auto"
                          >
                            <RotateCcw className="w-4 h-4" /> Reset to Default
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {campaigns.length === 0 && (
            <div className="text-center py-16 text-gray-500" data-testid="newsletters-empty">
              <Send className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p>No newsletter campaigns available.</p>
            </div>
          )}
        </div>
      </div>
    </Sidebar>
  );
};

export default AdminNewsletters;
